import React, {useState, useCallback} from 'react';
import classnames from 'classnames';
import logoBlue from 'assets/images/static/logo-blue.png';
import './new-public-navbar.scss';

const educatorsDropdown = [
  {path: '/educators', title: 'Counselors & Teachers'},
  {path: '/blog/educators-resources/', title: 'Educator Resources'},
];

const resourcesDropdown = [
  {path: 'https://help.raise.me', title: 'Help Center'},
  {path: '/blog/', title: 'Student Blog'},
  {path: 'https://campuslogic.com/company/', title: 'About Us'},
];

const renderDropdownItems = (items: {path: string; title: string}[]) =>
  items.map((item) => (
    <li key={item.path} className="public-navbar-submenu-item" role="none">
      <a className="public-navbar-submenu-link" href={item.path} role="menuitem">
        {item.title}
      </a>
    </li>
  ));

const NewPublicNavbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const loginPath = `/login?next=${location.pathname}`;
  const signupPath = `/signup?next=${location.pathname}`;

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const toggleDropdown = (name: string) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  return (
    <div className="no-top-padding sticky-nav-header-top">
      <nav className="public-navbar public-navbar-main new-navbar navbar-default" role="navigation">
        <div className="container public-navbar-container">
          <a href="/" className="public-navbar-logo">
            <img
              className="logo-blue logo public-navbar-logo-blue"
              width="118"
              src={logoBlue}
              alt="Go to Raise Me homepage"
            />
          </a>
          <ul className="public-navbar-menu-item-list navbar-flush-right">
            <li className="public-navbar-menu-item">
              <a href="/colleges/search" className="public-navbar-menu-button navbar-link-font">
                College Search
              </a>
            </li>
            <li className="public-navbar-menu-item">
              <a href="/microscholarships" className="public-navbar-menu-button navbar-link-font">
                Micro-Scholarships
              </a>
            </li>
            <li className="public-navbar-menu-item">
              <a href="/parents" className="public-navbar-menu-button navbar-link-font">
                Parents
              </a>
            </li>
            <li className="public-navbar-menu-item">
              <button
                className="public-navbar-menu-button navbar-link-font"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Educators
              </button>
              <ul
                className="public-navbar-dropdown-content public-navbar-dropdown-content-educators"
                role="menu"
                aria-label="Educators submenu"
              >
                {renderDropdownItems(educatorsDropdown)}
              </ul>
            </li>
            <li className="public-navbar-menu-item">
              <button
                className="public-navbar-menu-button navbar-link-font"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Resources
              </button>
              <ul
                className="public-navbar-dropdown-content public-navbar-dropdown-content-resources"
                role="menu"
                aria-label="Resources submenu"
              >
                {renderDropdownItems(resourcesDropdown)}
              </ul>
            </li>
            <li className="public-navbar-menu-item public-navbar-cta-item ml-8">
              <a href={loginPath} className="btn-navbar-secondary">
                Log In
              </a>
              <a href={signupPath} className="btn-navbar-primary">
                Sign Up
              </a>
            </li>
          </ul>
        </div>
      </nav>

      <nav className="public-navbar-mobile public-navbar navbar-default">
        <div
          className={classnames('public-navbar-overlay', {'shared-overlay': mobileMenuOpen})}
        ></div>
        <a href="/" className="public-navbar-logo">
          <img
            className="logo-blue logo public-navbar-logo-blue"
            width="96"
            src={logoBlue}
            alt="Go to Raise Me homepage"
          />
        </a>
        <button
          className={classnames('public-navbar-mobile-menu-button', {'open-menu': mobileMenuOpen})}
          data-testid="hamburger-menu-button"
          onClick={toggleMobileMenu}
        >
          Menu
          <div className="public-navbar-mobile-menu-icon">
            <span />
            <span />
            <span />
            <span />
          </div>
        </button>
        <div
          className={classnames('public-navbar-mobile-menu', {'show-mobile-menu': mobileMenuOpen})}
        >
          <div className="public-navbar-mobile-menu-container">
            <ul className="public-navbar-mobile-menu-list">
              <li className="public-navbar-menu-item">
                <a href="/colleges/search" className="public-navbar-menu-button">
                  College Search
                </a>
              </li>
              <li className="public-navbar-menu-item">
                <a href="/microscholarships" className="public-navbar-menu-button">
                  Micro-Scholarships
                </a>
              </li>
              <li className="public-navbar-menu-item">
                <a href="/parents" className="public-navbar-menu-button">
                  Parents
                </a>
              </li>
              <li
                className={classnames('public-navbar-menu-item', {
                  'show-dropdown-submenu': openDropdown === 'educators',
                })}
              >
                <button
                  className="public-navbar-menu-button"
                  aria-haspopup="true"
                  onClick={() => toggleDropdown('educators')}
                >
                  Educators
                  <i className="rm rm-chevron-down public-navbar-chevron" />
                </button>
                <ul
                  className="public-navbar-dropdown-content public-navbar-dropdown-content-educators"
                  aria-label="submenu"
                >
                  {renderDropdownItems(educatorsDropdown)}
                </ul>
              </li>
              <li
                className={classnames('public-navbar-menu-item', {
                  'show-dropdown-submenu': openDropdown === 'resources',
                })}
              >
                <button
                  className="public-navbar-menu-button"
                  aria-haspopup="true"
                  onClick={() => toggleDropdown('resources')}
                >
                  Resources
                  <i className="rm rm-chevron-down public-navbar-chevron" />
                </button>
                <ul
                  className="public-navbar-dropdown-content public-navbar-dropdown-content-resources"
                  aria-label="submenu"
                >
                  {renderDropdownItems(resourcesDropdown)}
                </ul>
              </li>
            </ul>
            <div className="public-navbar-mobile-cta-buttons">
              <a
                href={loginPath}
                aria-label="login (opens dialog)"
                className="shared-button small primary-outline public-navbar-mobile-signup-button"
                data-testid="mobile-login-button"
              >
                Log In
              </a>
              <a
                href={signupPath}
                aria-label="Sign Up (opens dialog)"
                className="shared-button small primary public-navbar-mobile-login-button"
                data-testid="mobile-signup-button"
              >
                Sign Up
              </a>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default NewPublicNavbar;
